<!--
// app/pages/[region]/contact-us.vue
-->
<script setup lang="ts">
import { useRegions } from "~/composables/regions";

definePageMeta({
  i18n: {
    paths: {
      en: "/[region]/contact-us",
      es: "/[region]/contacto",
      de: "/[region]/kontakt",
      fr: "/[region]/contacter",
      it: "/[region]/contatti",
      pt: "/[region]/contato",
    },
  },
  middleware: [
    "not-found-italy",
    "not-found-king-of-kings-nations",
    "not-found-france",
    "not-found-germany",
    "not-found-mena",
  ],
});

const { isSpain, isAmericas, isWorldCup, isNations, isBrazil } = useRegions();
const { t } = useI18n();

useSeoMeta({
  title: t("footer.nav.contact"),
  ogTitle: t("footer.nav.contact"),
});
</script>

<template>
  <section class="px-4 md:px-6 py-16">
    <div class="kql-w-container flex flex-col md:flex-row justify-between items-start gap-x-4">
      <div class="box-info-contact">
        <h1 class="title-box-contact">{{ $t("contact.contactUs.title") }}</h1>
        <p v-if="!isBrazil" class="text-lg text-gray-300 mb-6">
          <span class="text-sm text-gray-500">{{ $t("contact.contactPress.sentence") }}</span>
          <br />
          <a v-if="isSpain || isWorldCup || isNations" :href="`mailto:${$t('contact.contactPress.email')}`">
            {{ $t("contact.contactPress.email") }}
          </a>
          <a v-else-if="isAmericas" href="mailto:americasmedia@kingsleague.pro">
            americasmedia@kingsleague.pro
          </a>
        </p>
        <p class="text-lg text-gray-300 mb-6">
          <span class="text-sm text-gray-500">{{ $t("contact.contactCommercial.sentence") }}</span>
          <br />
          <a
            v-if="isSpain || isWorldCup || isNations || isBrazil"
            :href="`mailto:${$t('contact.contactCommercial.email')}`"
          >
            {{ $t("contact.contactCommercial.email") }}
          </a>
          <a v-else-if="isAmericas" href="mailto:americascomercial@kingsleague.pro">
            americascomercial@kingsleague.pro
          </a>
        </p>
        <p class="text-lg text-gray-300 mb-6">
          <span class="text-sm text-gray-500">{{ $t("contact.contactMoreInfo.sentence") }}</span>
          <br />
          <a
            v-if="isSpain || isWorldCup || isNations"
            :href="`mailto:${$t('contact.contactMoreInfo.email')}`"
          >
            {{ $t("contact.contactMoreInfo.email") }}
          </a>
          <a v-else-if="isAmericas" href="mailto:americasinfo@kingsleague.pro">
            americasinfo@kingsleague.pro
          </a>
          <a v-else-if="isBrazil" href="mailto:informacao@kingsleague.pro">informacao@kingsleague.pro</a>
        </p>
      </div>
      <div v-if="isSpain" class="box-info-contact mb-6">
        <p class="title-box-contact">
          {{ $t("contact.stadiumSpain.title") }}
        </p>
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d6320.837517931451!2d2.1410854317794485!3d41.32158505514599!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a49fd088452e79%3A0x22928de673cd64a4!2sCupra%20Arena%20%7C%20Kings%20League!5e1!3m2!1sca!2ses!4v1702838817782!5m2!1sca!2ses"
          width="100%"
          height="353"
          style="border: 0"
          allowfullscreen=""
          loading="lazy"
          referrerpolicy="no-referrer-when-downgrade"
        />
        <div class="mt-4 border border-amber-300 rounded-md p-4 text-amber-300">
          <span class="flex items-center justify-start gap-2 font-semibold mb-2">
            <svg
              class="w-4 h-4"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
              />
            </svg>
            {{ $t("contact.stadiumAccess.alert.title") }}</span
          >
          <p class="text-sm">{{ $t("contact.stadiumAccess.alert.text") }}</p>
        </div>

        <p class="text-xl mt-4 mb-6">{{ $t("contact.stadiumAccess.address") }}</p>
        <p class="text-gray-300 text-sm mb-4">{{ $t("contact.stadiumAccess.infoAccess") }}</p>
        <p class="text-gray-400 text-sm mb-4">{{ $t("contact.stadiumAccess.infoAccess2") }}</p>
        <p class="text-gray-400 text-sm mb-4">
          {{ $t("contact.stadiumAccess.infoAccess.publicTransport.title") }}
          <br />
          {{ $t("contact.stadiumAccess.infoAccess.publicTransport") }}
        </p>
        <p class="text-gray-400 text-sm">
          {{ $t("contact.stadiumAccess.infoAccess.parking.title") }}
          <br />
          {{ $t("contact.stadiumAccess.infoAccess.parking") }}
        </p>
      </div>
    </div>
  </section>
</template>

<style scoped>
.box-info-contact {
  @apply px-4 mb-9 rounded-lg md:bg-gray-900 md:px-8 md:py-9 w-full md:w-1/2;
}

.title-box-contact {
  @apply text-4xl font-bold leading-9 text-gray-200 mb-4;
}

a {
  @apply hover:text-secondary;
}
</style>
